import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useApi } from '@/composables/useApi'

import { useUserData } from '@/shared/composables/local-storage'
import { TIMEZONES } from '@/shared/lib/timezones'
import { Configuration } from '@/shared/types/api'
import { UserRoles } from '@/shared/types/router'
import { getAbsoluteWebSocketURL } from '@/shared/utils/url'

const DEFAULT_SIP_WS = getAbsoluteWebSocketURL('/sip')
const DEFAULT_GUEST_SIP_WS = getAbsoluteWebSocketURL('/gsip')

export const useStoreConfiguration = defineStore('configuration', () => {
  const configuration = ref<Configuration>({
    isWebClientFilesEnabled: true,
    sipWs: DEFAULT_SIP_WS,
    tzOffset: 180,
  })

  const { userRole } = useUserData()

  /** URL для SIP WebSocket: зависит от роли (гость → /gsip, иначе /sip). Реактивное свойство — после авторизации гостя без F5 подставляется /gsip. */
  const sipWs = computed(() => {
    const fromApi = configuration.value.sipWs
    const isGuestRole = userRole.value === UserRoles.GUEST
    if (isGuestRole) {
      return fromApi?.includes('gsip') ? fromApi : DEFAULT_GUEST_SIP_WS
    }
    return fromApi ?? DEFAULT_SIP_WS
  })

  const domainPath = computed(() => configuration.value.domainPath ?? 'ROOT')
  const filesEnabled = computed(() => configuration.value.isWebClientFilesEnabled)

  const load = async (): Promise<Configuration> => {
    try {
      const api = useApi()
      const { data } = await api.getConfiguration()
      const sipWs = data.sipWs ?? DEFAULT_SIP_WS
      configuration.value = { ...configuration.value, ...data, sipWs }
      return data
    } catch {
    }
    return configuration.value
  }

  const loadGuest = async (): Promise<Configuration> => {
    try {
      const api = useApi()
      const { data } = await api.getConfigurationGuest()
      const sipWs = data.sipWs ?? DEFAULT_GUEST_SIP_WS
      configuration.value = { ...configuration.value, ...data, sipWs }
      return data
    } catch {
    }
    return {
      isWebClientFilesEnabled: true,
      sipWs: DEFAULT_GUEST_SIP_WS,
      tzOffset: 180,
    }
  }

  const timezoneServer = computed(() => {
    const totalMinutes = configuration.value.tzOffset
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.abs(totalMinutes % 60)
    const sign = totalMinutes >= 0 ? '+' : '-'
    const timezoneStr = `${sign}${String(Math.abs(hours)).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    return TIMEZONES.find(timezone => timezone.label === timezoneStr)?.value
  })

  return {
    configuration,
    domainPath,
    filesEnabled,
    load,
    loadGuest,
    sipWs,
    timezoneServer,
  }
})
